import { NEXT_PUBLIC_BASE_URL } from '@env';
import { ProxyLinkSource } from '.cache/__types__';
import path from 'path';
import { FeatureFlagVariant, isExperimentRunning } from './feature-flag';

export class CardPageUrlGenerator {
  cardId: string | undefined;
  workspaceID: string | undefined;
  tab: 'card' | 'qr-code' | undefined = 'card';
  mode: 'view' | 'edit' | 'create' | undefined = 'view';
  creationMode: 'personal' | 'team' | undefined;
  duplicateCardId: string | undefined;

  setCardId(cardId: string | undefined): this {
    this.cardId = cardId;
    return this;
  }

  setTab(tab: 'card' | 'qr-code'): this {
    this.tab = tab;
    return this;
  }

  setMode(mode: 'view' | 'edit' | 'create'): this {
    this.mode = mode;
    return this;
  }

  setWorkspaceID(workspaceID: string | undefined): this {
    this.workspaceID = workspaceID;
    return this;
  }

  setCreationMode(creationMode: 'personal' | 'team'): this {
    this.creationMode = creationMode;
    return this;
  }

  setDuplicateCardID(duplicateCardId: string): this {
    this.duplicateCardId = duplicateCardId;
    return this;
  }

  generateUrl(): string {
    return this.url;
  }

  get url(): string {
    if (!this.workspaceID) throw new Error('Workspace ID is required');

    if (this.mode !== 'create' && !this.cardId) return `/${this.workspaceID}/cards`;
    if (this.mode === 'create') {
      if (!this.creationMode)
        throw new Error('Creation mode (personal, team) must be specified when in card create mode');

      let baseUrl = `/${this.workspaceID}/cards/new/${this.creationMode}`;
      if (this.tab !== 'card') baseUrl = `${baseUrl}?tab=${this.tab}`;
      if (this.duplicateCardId) {
        return `${baseUrl}?from=${this.duplicateCardId}&type=${this.creationMode}`;
      }

      return baseUrl;
    }
    if (this.tab === 'card' && this.mode === 'view') return `/${this.workspaceID}/cards/${this.cardId}`;
    if (this.tab === 'card' && this.mode === 'edit')
      return `/${this.workspaceID}/cards/${this.cardId}?mode=edit`;
    if (this.mode === 'view') return `/${this.workspaceID}/cards/${this.cardId}?tab=${this.tab}`;
    return `/${this.workspaceID}/cards/${this.cardId}?mode=${this.mode}&tab=${this.tab}`;
  }
}

export class CardPresetPageUrlGenerator {
  cardPresetId: string | undefined;
  workspaceID: string | undefined;
  tab: 'card' | 'qr-code' | undefined = 'card';
  mode: 'view' | 'edit' | 'create' | undefined = 'view';
  duplicateCardPresetId: string | undefined;

  setCardPresetId(cardPresetId: string | undefined): this {
    this.cardPresetId = cardPresetId;
    return this;
  }

  setTab(tab: 'card' | 'qr-code'): this {
    this.tab = tab;
    return this;
  }

  setMode(mode: 'view' | 'edit' | 'create'): this {
    this.mode = mode;
    return this;
  }

  setWorkspaceID(workspaceID: string | undefined): this {
    this.workspaceID = workspaceID;
    return this;
  }

  setDuplicateCardPresetID(duplicateCardPresetId: string): this {
    this.duplicateCardPresetId = duplicateCardPresetId;
    return this;
  }

  generateUrl(): string {
    return this.url;
  }

  get url(): string {
    if (!this.workspaceID) throw new Error('Workspace ID is required');

    if (this.mode !== 'create' && !this.cardPresetId) return `/${this.workspaceID}/presets`;
    if (this.mode === 'create') {
      let baseUrl = `/${this.workspaceID}/presets/new`;
      if (this.tab !== 'card') baseUrl = `${baseUrl}?tab=${this.tab}`;
      if (this.duplicateCardPresetId) return `${baseUrl}?from=${this.duplicateCardPresetId}`;

      return baseUrl;
    }
    if (this.tab === 'card' && this.mode === 'view')
      return `/${this.workspaceID}/presets/${this.cardPresetId}`;
    if (this.tab === 'card' && this.mode === 'edit')
      return `/${this.workspaceID}/presets/${this.cardPresetId}?mode=edit`;
    if (this.mode === 'view') return `/${this.workspaceID}/presets/${this.cardPresetId}?tab=${this.tab}`;
    return `/${this.workspaceID}/presets/${this.cardPresetId}?mode=${this.mode}&tab=${this.tab}`;
  }
}

const APP_STORE_ID = '1324102258';
const IOS_APP_BUNDLE_ID = 'ibi=com.rabbl.Blink';
const ANDROID_AP_PACKAGE_NAME = 'com.rabbl.blinq';

export function createDeepLink(
  link: string,
  iosMinimumVersion = '1.3.17',
  androidMinimumVersion = '1.2.12'
): string {
  const deepLink = `https://blinqapp.page.link/?link=${encodeURIComponent(
    link
  )}&ibi=${APP_STORE_ID}&isi=${IOS_APP_BUNDLE_ID}&apn=${ANDROID_AP_PACKAGE_NAME}&imv=${iosMinimumVersion}&amv=${androidMinimumVersion}`;

  return deepLink;
}

/**
 * Creates a phone number link that can be used in an anchor tag
 * @param phoneNumber The phone number to dial
 * @param extension The extension numbers to dial once the call has connected
 * @see https://stackoverflow.com/a/67810744/12385086
 */
export function createPhoneNumberLink(phoneNumber: string, extension?: string): string {
  const trimmedPhoneNumber = phoneNumber.replace(/[\s()-]/g, '');
  if (!extension) return `tel:${trimmedPhoneNumber}`;
  else return `tel:${trimmedPhoneNumber}, ${extension}`;
}

export const removeUrlScheme = (url: string): string => {
  if (url.startsWith('https://')) {
    return url.split(/https:\/\//)[1];
  }
  if (url.startsWith('http://')) {
    return url.split(/http:\/\//)[1];
  }
  return url;
};

export function buildUrl(...parts: string[]): string {
  return new URL(path.join(...parts)).toString();
}

export function removePortFromUrl(url: string): string {
  const urlObject = new URL(url);
  urlObject.port = '';
  return urlObject.toString();
}

export function replaceUrlHost(url: string, newHost: string): string {
  const parsedUrl = new URL(url);
  parsedUrl.host = newHost;
  return parsedUrl.toString();
}

/** Builds a card URL based on the current environment
 * Returns the public URL if no cardId is provided
 */
export function getCardUrl(
  cardId?: string,
  options?: { blinqSource?: ProxyLinkSource; isQrCode?: boolean }
): string {
  if (!cardId) {
    return NEXT_PUBLIC_BASE_URL;
  }
  let url = buildUrl(NEXT_PUBLIC_BASE_URL, cardId);
  if (options?.blinqSource) {
    url += `?bs=${options.blinqSource}`;
  }
  if (options?.isQrCode) {
    url = transformQrCardUrl(url);
  }
  return url;
}

/**
  Transforms a QR code's URL, which allows us to control its AASA invocation behaviour.
  @param url The original card URL to transform.
  @returns A new URL with the updated host and query params.
*/
function transformQrCardUrl(url: string): string {
  const newHost = getQrUrlHost();
  const newUrl = new URL(url.toString());

  // App Clip experiments are controlled via query params in the invocation link.
  const experimentName = 'ida_v';
  const experimentVariant = isExperimentRunning('ios_download_app', 'treatment') ? 'treatment' : 'control';
  newUrl.searchParams.append(experimentName, experimentVariant);

  return replaceUrlHost(newUrl.toString(), newHost);
}

/**
  Determines which URL host to use in our QR code links, which allows us to control AASA invocation behaviour.
    - `blinq.me` (no subdomain) is used as default / control.
    - `s`, `s1`, `s2`, `s3` subdomains are used as treatments.
    - `s0` subdomain is used to disable app clip (stable fallback).
  @see https://www.notion.so/blinq-me/AASA-Files-Problem-Solution-0c31c86de059420cad5c21b3e78852be
  @see https://app.amplitude.com/experiment/blinq-hq/432635/config/303500/settings
*/
function getQrUrlHost(): string {
  const host = removeUrlScheme(NEXT_PUBLIC_BASE_URL);
  const experimentName = 'qr_link_subdomains';
  const experimentVariants = {
    control: host,
    share: `${Subdomain.Share}.${host}`,
    share1: `${Subdomain.Share1}.${host}`,
    share2: `${Subdomain.Share2}.${host}`,
    share3: `${Subdomain.Share3}.${host}`,
    fallback: `${Subdomain.Share0}.${host}`,
  };

  for (const [variant, subdomain] of Object.entries(experimentVariants)) {
    if (isExperimentRunning(experimentName, variant as FeatureFlagVariant[typeof experimentName])) {
      return subdomain;
    }
  }

  return host;
}

export enum Subdomain {
  Share = 's',
  Share1 = 's1',
  Share2 = 's2',
  Share3 = 's3',
  Share0 = 's0',
}
